import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BudgetBucket } from "./types/budget.types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { BucketFormData, createInitialBucketFormData } from "./components/forms/BucketFormData";
import { AddBucketDialog } from "./components/dialogs/AddBucketDialog";
import { EditBucketDialog } from "./components/dialogs/EditBucketDialog";
import { DeleteBucketDialog } from "./components/dialogs/DeleteBucketDialog";

interface BudgetFormProps {
  totalBudget: number;
  buckets: BudgetBucket[];
  onAddBucket: (name: string, budget: number) => Promise<void>;
  onUpdateBucket: (id: string, name: string, budget: number, description: string) => Promise<void>;
  onDeleteBucket: (id: string) => Promise<void>;
  onAddSubcategory: (bucketId: string, name: string, budget: number, expRomi: string, kpi: string, description: string, expCompletion: string | null) => Promise<void>;
  onUpdateSubcategory: (bucketId: string, subcategoryId: string, name: string, budget: number, expRomi: string, kpi: string, description: string, expCompletion: string | null) => Promise<void>;
  onDeleteSubcategory: (bucketId: string, subcategoryId: string) => Promise<void>;
}

export const BudgetForm: React.FC<BudgetFormProps> = ({
  totalBudget,
  buckets,
  onAddBucket,
  onUpdateBucket,
  onDeleteBucket
}) => {
  const [isAddBucketOpen, setIsAddBucketOpen] = useState<boolean>(false);
  const [isEditBucketOpen, setIsEditBucketOpen] = useState<boolean>(false);
  const [isDeleteBucketOpen, setIsDeleteBucketOpen] = useState<boolean>(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState<boolean>(false);
  const [selectedBucket, setSelectedBucket] = useState<BudgetBucket | null>(null);
  const [bucketFormData, setBucketFormData] = useState<BucketFormData>(createInitialBucketFormData());
  const [tableCollapsed, setTableCollapsed] = useState<boolean>(true);

  // Standard buckets
  const standardBuckets = [
    { name: "Public Relations (PR)", defaultAllocation: 15 },
    { name: "Analyst Relations (AR)", defaultAllocation: 10 },
    { name: "Field Marketing", defaultAllocation: 25 },
    { name: "Marcomms", defaultAllocation: 20 },
    { name: "Digital Marketing", defaultAllocation: 20 },
    { name: "Other Initiatives", defaultAllocation: 10 }
  ];

  // Function to handle form data changes
  const handleBucketFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setBucketFormData(prev => ({ ...prev, [name]: value }));

    // Update budget based on percentage if allocation percentage was just changed
    if (name === "allocationPercentage") {
      const percentage = parseFloat(value);
      if (!isNaN(percentage) && totalBudget) {
        const calculatedBudget = Math.round(percentage / 100 * totalBudget);
        setBucketFormData(prev => ({ ...prev, budget: calculatedBudget.toString() }));
      }
    }
  };

  // Handle allocation percentage change via slider
  const handleAllocationSliderChange = (value: number[]) => {
    const percentage = value[0];
    setBucketFormData(prev => ({ ...prev, allocationPercentage: percentage }));
    if (totalBudget) {
      const calculatedBudget = Math.round(percentage / 100 * totalBudget);
      setBucketFormData(prev => ({ ...prev, budget: calculatedBudget.toString() }));
    }
  };

  // Handle budget change and update percentage
  const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setBucketFormData(prev => ({ ...prev, budget: value }));
    if (totalBudget) {
      const budget = parseFloat(value);
      if (!isNaN(budget)) {
        const calculatedPercentage = budget / totalBudget * 100;
        setBucketFormData(prev => ({ ...prev, allocationPercentage: calculatedPercentage }));
      }
    }
  };

  // Handle select bucket change
  const handleBucketSelect = (value: string) => {
    if (value === "custom") {
      setBucketFormData(prev => ({ ...prev, name: "custom", allocationPercentage: 0, budget: "0" }));
    } else {
      const standardBucket = standardBuckets.find(b => b.name === value);
      if (standardBucket) {
        const recommendedBudget = Math.round(standardBucket.defaultAllocation / 100 * totalBudget);
        setBucketFormData({
          name: value,
          budget: recommendedBudget.toString(),
          customName: "",
          allocationPercentage: standardBucket.defaultAllocation
        });
      }
    }
  };

  // Open add bucket dialog
  const openAddBucketDialog = () => {
    setBucketFormData(createInitialBucketFormData());
    setIsAddBucketOpen(true);
  };

  // Open edit bucket dialog
  const openEditBucketDialog = (bucket: BudgetBucket) => {
    setSelectedBucket(bucket);
    const allocationPercentage = totalBudget > 0 ? bucket.budget / totalBudget * 100 : 0;
    setBucketFormData({
      name: "custom",
      budget: bucket.budget.toString(),
      customName: bucket.name,
      allocationPercentage: allocationPercentage
    });
    setIsEditBucketOpen(true);
  };

  // Open delete bucket dialog
  const openDeleteBucketDialog = (bucket: BudgetBucket) => {
    setSelectedBucket(bucket);
    setIsDeleteBucketOpen(true);
  };

  // Handle adding a new bucket
  const handleAddBucket = async () => {
    const budget = parseInt(bucketFormData.budget);
    const bucketName = bucketFormData.name === "custom" ? bucketFormData.customName : bucketFormData.name;
    if (!bucketName || isNaN(budget) || budget <= 0) {
      return;
    }
    await onAddBucket(bucketName, budget);
    setIsAddBucketOpen(false);
  };

  // Handle updating a bucket
  const handleUpdateBucket = async () => {
    const budget = parseInt(bucketFormData.budget);
    const bucketName = bucketFormData.name === "custom" ? bucketFormData.customName : bucketFormData.name;
    if (!selectedBucket || !bucketName || isNaN(budget) || budget <= 0) {
      return;
    }
    await onUpdateBucket(selectedBucket.id, bucketName, budget, '');
    setIsEditBucketOpen(false);
  };

  // Handle deleting a bucket
  const handleDeleteBucket = async () => {
    if (!selectedBucket) {
      return;
    }
    await onDeleteBucket(selectedBucket.id);
    setIsDeleteBucketOpen(false);
  };

  // Calculate total allocated and remaining budget
  const allocatedBudget = buckets.reduce((sum, bucket) => sum + bucket.budget, 0);
  const remainingBudget = totalBudget - allocatedBudget;

  // Sort buckets alphabetically for display
  const sortedBuckets = [...buckets].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-secondary/10 border-2 border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-primary">Budget Status</CardTitle>
            <CardDescription className="text-primary-light">Current allocation status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white">Total Budget:</span>
                  <span className="font-bold text-white">${totalBudget.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white">Allocated:</span>
                  <span className="font-bold text-primary">${allocatedBudget.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white">Remaining:</span>
                  <span className="font-bold text-yellow-500">${remainingBudget.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="pt-4 border-t border-secondary/20">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-white">Allocation Progress</span>
                  <span className="text-xs text-primary-light">
                    {Math.round(allocatedBudget / totalBudget * 100)}% of total
                  </span>
                </div>
                <div className="h-2 bg-secondary/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full" 
                    style={{
                      width: `${allocatedBudget / totalBudget * 100}%`,
                      maxWidth: "100%"
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-2 bg-secondary/10 border-2 border-primary h-[400px]">
          <CardHeader className="pb-0">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-primary">Actions</CardTitle>
                <CardDescription className="text-primary-light">Manage your marketing budget allocations</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Dialog open={isHelpDialogOpen} onOpenChange={setIsHelpDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1">
                      <HelpCircle className="h-4 w-4" />
                      <span>Help</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="text-primary">Budgeting Help</DialogTitle>
                      <DialogDescription className="text-black">
                        How to manage your marketing budget effectively
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <h3 className="font-medium text-primary">Budget Management Process</h3>
                      <ol className="list-decimal pl-5 space-y-2">
                        <li>Start by setting your overall marketing budget in the Budget Summary tab</li>
                        <li>Create budget buckets (major categories) using the standard categories or custom ones</li>
                        <li>Add subcategories to each bucket with specific budgets and KPIs</li>
                        <li>Track allocation vs. available budget using the Budget Allocation tab</li>
                        <li>View detailed breakdowns and make adjustments as needed</li>
                      </ol>
                      
                      <div className="pt-2 mt-4 border-t">
                        <h3 className="font-medium mb-2 text-primary">Standard Marketing Buckets</h3>
                        <ul className="space-y-2">
                          {standardBuckets.map(bucket => (
                            <li key={bucket.name} className="flex justify-between">
                              <span>{bucket.name}:</span>
                              <span className="text-primary font-medium">{bucket.defaultAllocation}%</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Button onClick={openAddBucketDialog} className="gap-1">
                  <Plus className="h-4 w-4" />
                  <span>Add Bucket</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-secondary/5 p-0 rounded-md py-0 my-[7px]">
              <h3 className="font-medium mb-3 text-white">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[...standardBuckets].sort((a, b) => a.name.localeCompare(b.name)).map(bucket => {
                  const isAlreadyAdded = buckets.some(b => b.name === bucket.name);
                  const recommendedBudget = Math.round(bucket.defaultAllocation / 100 * totalBudget);
                  const existingBucket = buckets.find(b => b.name === bucket.name);
                  
                  return (
                    <Card key={bucket.name} className={`bg-secondary/20 border border-secondary/40 ${isAlreadyAdded ? 'opacity-50' : ''}`}>
                      <CardContent className="p-2 py-px">
                        <div className="flex justify-between items-center">
                          <h4 className="text-sm font-medium text-white">{bucket.name}</h4>
                          <span className="text-xs text-primary font-medium">{bucket.defaultAllocation}%</span>
                        </div>
                        <p className="text-xs text-primary-light mt-1">
                          Recommended: ${recommendedBudget.toLocaleString()}
                        </p>
                        {isAlreadyAdded && existingBucket && (
                          <div className="mt-1 text-[10px] text-gray-300">
                            <div className="flex justify-between">
                              <span>Budgeted Amount:</span>
                              <span>${existingBucket.budget.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Remaining Unallocated Budget:</span>
                              <span>${existingBucket.available.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Number Of Sub Categories:</span>
                              <span>{existingBucket.subcategories.length}</span>
                            </div>
                          </div>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          disabled={isAlreadyAdded} 
                          onClick={() => {
                            if (!isAlreadyAdded) {
                              onAddBucket(bucket.name, recommendedBudget);
                            }
                          }} 
                          className="w-full mt-2 h-7 text-xs bg-primary-dark"
                        >
                          {isAlreadyAdded ? 'Already Added' : 'Quick Add'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="bg-secondary/10 border-2 border-primary">
        <div className="flex justify-between items-center cursor-pointer p-4 border-b border-secondary/20" onClick={() => setTableCollapsed(!tableCollapsed)}>
          <div>
            <h3 className="text-lg font-bold text-primary">Budget Allocation Management</h3>
            <p className="text-sm text-white">Manage your budget buckets and their allocations</p>
          </div>
          <p className="text-sm text-white">(Click here to expand or collapse the table view)</p>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary-light">
            {tableCollapsed ? <ChevronDown className="h-4 w-4 text-primary-light" /> : <ChevronUp className="h-4 w-4 text-primary-light" />}
          </Button>
        </div>
        {!tableCollapsed && (
          <CardContent className="pt-4">
            {buckets.length === 0 ? (
              <div className="text-center text-[11px] py-10 bg-secondary/5 rounded-md text-primary-light">
                <p className="text-primary-light mb-4">No budget buckets defined yet</p>
                <Button onClick={openAddBucketDialog}>Create First Budget Bucket</Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-secondary/5 text-[11px] text-left">
                    <TableHead>Budget Bucket</TableHead>
                    <TableHead>Allocation</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Subcategories</TableHead>
                    <TableHead>Available</TableHead>
                    <TableHead className="text-right mx-0 px-[36px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBuckets.map(bucket => {
                    const allocationPercent = totalBudget > 0 ? bucket.budget / totalBudget * 100 : 0;
                    return (
                      <TableRow key={bucket.id} className="hover:bg-secondary/5 text-white text-[11px] text-left">
                        <TableCell className="font-medium">{bucket.name}</TableCell>
                        <TableCell>{allocationPercent.toFixed(1)}%</TableCell>
                        <TableCell>${bucket.budget.toLocaleString()}</TableCell>
                        <TableCell>{bucket.subcategories.length}</TableCell>
                        <TableCell>${bucket.available.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="items-left text-[11px] text-right">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8" 
                              onClick={() => openEditBucketDialog(bucket)}
                            >
                              <Edit2 className="h-3 w-3 mr-1 text-primary-light text-right" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 text-destructive" 
                              onClick={() => openDeleteBucketDialog(bucket)}
                            >
                              <Trash2 className="h-3 w-3 mr-1 text-primary-light text-right" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>
      
      <AddBucketDialog
        isOpen={isAddBucketOpen}
        onClose={() => setIsAddBucketOpen(false)}
        formData={bucketFormData}
        standardBuckets={standardBuckets}
        existingBuckets={buckets}
        totalBudget={totalBudget}
        remainingBudget={remainingBudget}
        onFormChange={handleBucketFormChange}
        onBucketSelect={handleBucketSelect}
        onAllocationSliderChange={handleAllocationSliderChange}
        onBudgetChange={handleBudgetChange}
        onAdd={handleAddBucket}
      />
      
      <EditBucketDialog
        isOpen={isEditBucketOpen}
        onClose={() => setIsEditBucketOpen(false)}
        formData={bucketFormData}
        selectedBucket={selectedBucket}
        onFormChange={handleBucketFormChange}
        onAllocationSliderChange={handleAllocationSliderChange}
        onBudgetChange={handleBudgetChange}
        onUpdate={handleUpdateBucket}
      />
      
      <DeleteBucketDialog
        isOpen={isDeleteBucketOpen}
        onClose={() => setIsDeleteBucketOpen(false)}
        selectedBucket={selectedBucket}
        onDelete={handleDeleteBucket}
      />
    </div>
  );
};
